import { concat, hash } from '@ember/helper';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import calculatePosition from 'ember-basic-dropdown/utils/calculate-position';
import { task, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { modifier } from 'ember-modifier';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import BvPopoverBody from 'volta/components/bv-popover/body';
import BvPopoverFooter from 'volta/components/bv-popover/footer';
import BvPopoverHeader from 'volta/components/bv-popover/header';
import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';
import { closest, outerHeight, windowSize } from 'volta/utils/dom-utils';

import type { ComponentLike, ModifierLike } from '@glint/template';
import type { BasicDropdownArgs, Dropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import type { BasicDropdownContentSignature } from 'ember-basic-dropdown/components/basic-dropdown-content';
import type { BasicDropdownTriggerSignature } from 'ember-basic-dropdown/components/basic-dropdown-trigger';
import type { CalculatePositionResult } from 'ember-basic-dropdown/utils/calculate-position';
import type { FocusTrapOptions } from 'global';
import type { BvPopoverBodySignature } from 'volta/components/bv-popover/body';
import type { BvPopoverFooterSignature } from 'volta/components/bv-popover/footer';
import type { TSize } from 'volta/utils/style-utils';
import type { BvPopoverHeaderSignature } from './header';

const SIZES: TSize[] = ['sm', 'md', 'xl', 'lg'];

interface TriggerModifierSignature {
  Args: {
    Positional: [];
    Named: {};
  };
  Element: HTMLElement;
}

export type BvPopoverApi = {
  api: Dropdown;
  uniqueId: Dropdown['uniqueId'];
  isOpen: Dropdown['isOpen'];
  disabled: Dropdown['disabled'];
  open: Dropdown['actions']['open'];
  close: Dropdown['actions']['close'];
  toggle: Dropdown['actions']['toggle'];
  reposition: Dropdown['actions']['reposition'];
  trigger: ModifierLike<TriggerModifierSignature>;
  focusTrapOptions: FocusTrapOptions;
  Trigger: ComponentLike<BasicDropdownTriggerSignature>;
  Content: ComponentLike<BasicDropdownContentSignature>;
  Header: ComponentLike<BvPopoverHeaderSignature>;
  Body: ComponentLike<BvPopoverBodySignature>;
  Footer: ComponentLike<BvPopoverFooterSignature>;
};

export interface BvPopoverSignature {
  Args: BasicDropdownArgs & {
    size?: TSize;
    triggerRole?: string;
    required?: string;
    tabindex?: string;
    title?: string;
    ariaInvalid?: boolean;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    triggerId?: string;
    stopPropagation?: boolean;
    overlay?: boolean;
    isMenu?: boolean;
    popoverClass?: string;
    showCloseButton?: boolean;
    initialFocusElement?: FocusTrapOptions['initialFocus'];
    applyCustomPosition?: (calcResult: CalculatePositionResult) => CalculatePositionResult;
    onDropdownChange?: (ddApi: Dropdown | null) => void;
  };
  Blocks: {
    default: [BvPopoverApi];
  };
  Element: HTMLElement;
}

const handleTriggerKeyDown = (dropdown?: Dropdown, event?: KeyboardEvent) => {
  if (event?.key === 'ArrowDown' || event?.key === 'ArrowUp') {
    event.preventDefault();
    dropdown?.actions.open(event);
  }
};

const ANIMATION_TIMEOUT = 200;

/**
 * @typedef {Object} BvPopoverArgs
 * @property {string} popover content width (see SIZES)
 * @property {boolean} isMenu Is the popover a menu
 * @property {string} verticalPosition
 * @property {string} horizontalPosition
 * @property {boolean} matchTriggerWidth
 * @property {boolean} renderInPlace
 * @property {boolean} showCloseButton
 */
export default class BvPopoverComponent extends Component<BvPopoverSignature> {
  uniqueId = `${guidFor(this)}BvPopover`;
  dropdownId!: string;
  triggerId!: string;
  publicAPI!: Dropdown;

  get size() {
    const { size } = this.args;
    return size && SIZES.includes(size) ? size : undefined;
  }

  @action
  onInit(publicAPI: Dropdown) {
    this.setupDropdown(publicAPI);
    return this.args.onInit?.(publicAPI);
  }

  private setupDropdown(publicAPI: Dropdown) {
    this.publicAPI = publicAPI;
    this.uniqueId = publicAPI.uniqueId;
    this.dropdownId = `ember-basic-dropdown-content-${this.uniqueId}`;
    this.triggerId = `${publicAPI.uniqueId}-trigger`;
  }

  /**
   * Makes sure the popover is not taller than the available height
   * @param trigger
   * @param {HTMLElement} content The .bv-popover DOM element
   * @param destination
   * @param options
   */
  @action
  calculatePosition(
    trigger: Element,
    content: HTMLElement,
    destination: HTMLElement,
    options: Parameters<typeof calculatePosition>[3]
  ) {
    const pos: CalculatePositionResult = calculatePosition(trigger, content, destination, options);

    if (content && !this.args.renderInPlace) {
      // Here we have to use height property and not max-height because of
      // how IE 11 behaves when a container only has a max-height value and
      // children elements use flexbox layout
      content.style.height = 'auto';
      const windowHeight = windowSize().height;
      const elHeight = outerHeight(content);
      const maxHeight = windowHeight - (pos.style.top ?? 0);
      pos.style.height = elHeight >= maxHeight ? maxHeight : undefined;
      pos.style['max-height'] = maxHeight + 'px';

      if (this.args.applyCustomPosition) {
        return this.args.applyCustomPosition(pos);
      }
    }

    return pos;
  }

  @action
  handleOpen(ddApi: Dropdown, e?: Event) {
    return this.args.onOpen?.(ddApi, e);
  }

  @action
  handleClose(ddApi: Dropdown, e?: Event) {
    if (e?.target && elementIsInChild(e.target as HTMLElement, this.dropdownId)) {
      return false;
    }
    return this.args.onClose?.(ddApi, e);
  }

  @action
  handleDropdownChange(ddApi: Dropdown | null) {
    if (ddApi) {
      this.setupDropdown(ddApi);
      this.args.onDropdownChange?.(ddApi);
    }
  }

  /**
   * Modifiers
   */

  checkCanFocusTrap = task(async (_containers: Array<HTMLElement | SVGElement>) => {
    await timeout(ANIMATION_TIMEOUT);
    return;
  });

  triggerModifier = modifier<TriggerModifierSignature>((element: HTMLElement) => {
    element.dataset['ebdId'] = this.triggerId;
    element.setAttribute('aria-owns', this.dropdownId);
    element.setAttribute('role', this.args.triggerRole ?? 'button');
    element.setAttribute('aria-required', `${this.args.required ?? false}`);

    const handleKeyDown = (event: KeyboardEvent) => {
      handleTriggerKeyDown(this.publicAPI, event);
    };
    if (!this.args.disabled) {
      element.setAttribute('tabindex', this.args.tabindex || '0');
      element.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
  });

  <template>
    <BasicDropdown
      @calculatePosition={{this.calculatePosition}}
      @horizontalPosition={{orElse @horizontalPosition 'auto'}}
      @matchTriggerWidth={{orElse @matchTriggerWidth false}}
      @triggerComponent={{@triggerComponent}}
      @destination={{@destination}}
      @initiallyOpened={{@initiallyOpened}}
      @renderInPlace={{@renderInPlace}}
      @onOpen={{this.handleOpen}}
      @onClose={{this.handleClose}}
      @verticalPosition={{@verticalPosition}}
      @preventScroll={{orElse @preventScroll true}}
      @disabled={{@disabled}}
      @onInit={{this.onInit}}
      @registerAPI={{this.handleDropdownChange}}
      ...attributes
      as |dd|
    >

      {{#let
        (component
          (ensureSafeComponent dd.Trigger this)
          title=@title
          aria-describedby=@ariaDescribedBy
          aria-invalid=@ariaInvalid
          aria-label=@ariaLabel
          aria-labelledby=@ariaLabelledBy
          aria-required=@required
          role=(or @triggerRole 'button')
          id=@triggerId
          tabindex=(and (not @disabled) (or @tabindex '0'))
          stopPropagation=@stopPropagation
          onKeyDown=handleTriggerKeyDown
        )
        (component
          (ensureSafeComponent dd.Content this)
          overlay=@overlay
          defaultClass=(classNames
            'bv-popover'
            (if this.size (concat 'bv-popover--' this.size))
            (if @isMenu 'bv-popover--menu')
            @popoverClass
          )
        )
        as |TriggerComponent ContentComponent|
      }}
        {{yield
          (hash
            api=dd
            uniqueId=dd.uniqueId
            isOpen=dd.isOpen
            disabled=dd.disabled
            open=dd.actions.open
            close=dd.actions.close
            toggle=dd.actions.toggle
            reposition=dd.actions.reposition
            trigger=this.triggerModifier
            Trigger=TriggerComponent
            Content=ContentComponent
            Header=(component BvPopoverHeader showCloseButton=@showCloseButton popover=dd)
            Body=(component BvPopoverBody popover=dd)
            Footer=(component BvPopoverFooter popover=dd)
            focusTrapOptions=(hash
              clickOutsideDeactivates=false
              checkCanFocusTrap=(perform this.checkCanFocusTrap)
              onDeactivate=dd.actions.close
              escapeDeactivates=true
              allowOutsideClick=true
              initialFocus=@initialFocusElement
            )
          )
        }}
      {{/let}}
    </BasicDropdown>
  </template>
}

/**
 * Evaluates if the given element is in a child dropdown.
 *
 * @param {HTMLElement} el
 * @param {String} dropdownId
 */
function elementIsInChild(el: HTMLElement, dropdownId: string): boolean {
  const currentDD = document.getElementById(dropdownId);
  return elementIsInDropDown(el, currentDD);
}

function elementIsInDropDown(el?: HTMLElement | null, currentDD?: HTMLElement | null): boolean {
  if (!currentDD || !el) {
    return false;
  }

  // 1. Get the closest parent drop down
  const parentDD = closest(el, '.ember-basic-dropdown-content');
  // 2. Get the parent trigger
  const parentTrigger = parentDD?.id
    ? document.querySelector(`[aria-owns=${parentDD?.id}]`)
    : undefined;
  // 3. Is in current Dropdown
  const isInCurrentDD = parentTrigger && currentDD?.contains(parentTrigger);

  if (isInCurrentDD) {
    return true;
  } else if (parentTrigger) {
    return elementIsInDropDown(parentTrigger as HTMLElement, currentDD);
  } else return false;
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvPopover: typeof BvPopoverComponent;
    'bv-popover': typeof BvPopoverComponent;
  }
}
